import { useNavigate } from 'react-router-dom';
import {
  Users, Clock, DollarSign, MessageSquare,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { TeamStatus, type Team } from '../types/team';
import { InstanceStatus } from '../types/instance';

const STATUS_STYLES: Record<TeamStatus, string> = {
  [TeamStatus.ACTIVE]: 'bg-green-100 text-green-700',
  [TeamStatus.PAUSED]: 'bg-yellow-100 text-yellow-700',
  [TeamStatus.COMPLETED]: 'bg-slate-100 text-slate-600',
  [TeamStatus.FAILED]: 'bg-red-100 text-red-700',
};

const ROLE_COLORS: Record<string, string> = {
  creator: 'bg-blue-500',
  executor: 'bg-green-500',
  researcher: 'bg-purple-500',
};

const INSTANCE_STATUS_RING: Record<string, string> = {
  [InstanceStatus.RUNNING]: 'ring-green-400',
  [InstanceStatus.PAUSED]: 'ring-yellow-400',
  [InstanceStatus.STARTING]: 'ring-blue-400',
  [InstanceStatus.PENDING]: 'ring-blue-300',
  [InstanceStatus.COMPLETED]: 'ring-slate-300',
  [InstanceStatus.STOPPED]: 'ring-slate-300',
  [InstanceStatus.FAILED]: 'ring-red-400',
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function HealthIndicator({ team }: { team: Team }) {
  const instances = team.instances ?? [];
  if (instances.length === 0) return null;

  const running = instances.filter(i => i.status === InstanceStatus.RUNNING).length;
  const failed = instances.filter(i => i.status === InstanceStatus.FAILED).length;
  const paused = instances.filter(i => i.status === InstanceStatus.PAUSED).length;

  if (failed > 0) {
    return (
      <div className="flex items-center gap-1 text-red-500" title={`${failed} failed`}>
        <XCircle className="h-4 w-4" />
      </div>
    );
  }
  if (paused > 0) {
    return (
      <div className="flex items-center gap-1 text-yellow-500" title={`${paused} paused`}>
        <AlertTriangle className="h-4 w-4" />
      </div>
    );
  }
  if (running === instances.length) {
    return (
      <div className="flex items-center gap-1 text-green-500" title="All running">
        <CheckCircle2 className="h-4 w-4" />
      </div>
    );
  }
  return null;
}

export default function TeamCard({ team, messageCount }: { team: Team; messageCount?: number }) {
  const navigate = useNavigate();
  const instances = team.instances ?? [];
  const totalCost = instances.reduce((sum, i) => sum + Number(i.totalCostUsd), 0);

  return (
    <button
      onClick={() => navigate(`/teams/${team.id}`)}
      className="w-full rounded-xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-slate-300 hover:shadow-sm"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900">{team.name}</h3>
            <HealthIndicator team={team} />
          </div>
          <span
            className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[team.status] ?? 'bg-slate-100 text-slate-600'}`}
          >
            {team.status}
          </span>
        </div>
        {messageCount !== undefined && messageCount > 0 && (
          <div className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
            <MessageSquare className="h-3 w-3" />
            {messageCount}
          </div>
        )}
      </div>

      {team.description && (
        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{team.description}</p>
      )}

      {/* Agent avatars */}
      {instances.length > 0 && (
        <div className="mt-3 flex -space-x-2">
          {instances.slice(0, 6).map(inst => {
            const name = inst.agent?.name ?? '?';
            const role = inst.agent?.role ?? 'executor';
            const bg = ROLE_COLORS[role] || ROLE_COLORS.executor;
            const ring = INSTANCE_STATUS_RING[inst.status] || 'ring-slate-300';

            return (
              <div
                key={inst.id}
                className={`flex h-8 w-8 items-center justify-center rounded-full ring-2 ${bg} ${ring}`}
                title={`${name} (${inst.status})`}
              >
                <span className="text-[10px] font-bold text-white">{getInitials(name)}</span>
              </div>
            );
          })}
          {instances.length > 6 && (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 ring-2 ring-white">
              <span className="text-[10px] font-medium text-slate-600">+{instances.length - 6}</span>
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          {instances.length}
        </span>
        {totalCost > 0 && (
          <span className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            {totalCost.toFixed(2)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {relativeTime(team.createdAt)}
        </span>
      </div>
    </button>
  );
}
